import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "./i18n";

const NON_DEFAULT = SUPPORTED_LOCALES.filter((l) => l !== DEFAULT_LOCALE);
const HREFLANG_REGION = { en: "en", de: "de-DE", ja: "ja-JP" };

/**
 * Reads the current locale from the URL pathname.
 * `/de/...` → "de", `/ja/...` → "ja", everything else → "en".
 */
export function readLocaleFromPath(pathname) {
  const seg = (pathname || "/").split("/").filter(Boolean)[0];
  return NON_DEFAULT.includes(seg) ? seg : DEFAULT_LOCALE;
}

/**
 * Strip the locale prefix from a pathname, returning the bare path with leading slash.
 * `/de/resources` → `/resources`, `/ja` → `/`, `/` → `/`.
 */
export function stripLocale(pathname) {
  const parts = (pathname || "/").split("/").filter(Boolean);
  if (parts.length && NON_DEFAULT.includes(parts[0])) parts.shift();
  return "/" + parts.join("/");
}

/**
 * Prepend the locale prefix to a path. EN stays bare.
 */
export function withLocale(path, locale) {
  if (!path) path = "/";
  if (locale === DEFAULT_LOCALE) return path;
  if (!path.startsWith("/")) path = "/" + path;
  // Avoid double prefixing
  if (path === `/${locale}` || path.startsWith(`/${locale}/`)) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/**
 * Hook: returns the current locale + a localizer `L(path)` that prepends the locale.
 */
export function useLocale() {
  const { pathname } = useLocation();
  const locale = readLocaleFromPath(pathname);
  const L = (path) => withLocale(path, locale);
  return { locale, L };
}

/**
 * Sync i18next language with URL locale + update <html lang> and hreflang links.
 */
export function LocaleSync() {
  const { i18n } = useTranslation();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const locale = readLocaleFromPath(pathname);

  useEffect(() => {
    if (i18n.language !== locale) i18n.changeLanguage(locale);
    if (typeof document !== "undefined") {
      document.documentElement.lang = HREFLANG_REGION[locale] || locale;
    }
  }, [locale, i18n]);

  // Maintain hreflang links in <head>
  useEffect(() => {
    if (typeof document === "undefined") return;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
    const bare = stripLocale(pathname);
    // Remove existing managed link tags
    document.querySelectorAll('link[data-i18n-managed="true"]').forEach((n) => n.remove());
    SUPPORTED_LOCALES.forEach((lc) => {
      const href = `${base}${withLocale(bare, lc)}${search || ""}${hash || ""}`;
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = HREFLANG_REGION[lc] || lc;
      link.href = href;
      link.setAttribute("data-i18n-managed", "true");
      document.head.appendChild(link);
    });
    // x-default → English
    const xdef = document.createElement("link");
    xdef.rel = "alternate";
    xdef.hreflang = "x-default";
    xdef.href = `${base}${withLocale(bare, DEFAULT_LOCALE)}${search || ""}${hash || ""}`;
    xdef.setAttribute("data-i18n-managed", "true");
    document.head.appendChild(xdef);
  }, [pathname, search, hash]);

  // Expose a navigate helper through a custom event so external scripts (none currently) can dispatch
  useEffect(() => {
    window.__spiruvaNavigate = (path) => navigate(path);
    return () => { delete window.__spiruvaNavigate; };
  }, [navigate]);

  return null;
}

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
